import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import QRCode from 'react-qr-code';
import './ConnectModal.css';

const loadTone = (load) => {
  const value = Number(load || 0);
  if (value < 50) return 'low';
  if (value < 75) return 'mid';
  return 'high';
};

const ConnectModal = ({ node, onClose, onSuccess }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [config, setConfig] = useState(null);
  const [showQR, setShowQR] = useState(true);
  const [copied, setCopied] = useState(false);

  const locationLabel = node?.location || node?.country || t('connectModal.serverFallback');
  const flag = node?.flag_emoji || node?.country_flag || '🌐';
  const load = Number(node?.load_percent || 0);

  const title = useMemo(
    () =>
      config
        ? t('connectModal.titleConnected', { location: locationLabel })
        : t('connectModal.titleConnect', { location: locationLabel }),
    [config, locationLabel, t]
  );

  const handleQuickConnect = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await fetch('/api/vpn/quick-connect', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ node_id: node.id }),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || data.message || t('connectModal.connectFailed'));
      }

      setConfig({
        content: data.config,
        details: data.details,
      });

      if (onSuccess) onSuccess(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const downloadConfig = () => {
    if (!config?.content) return;
    const blob = new Blob([config.content], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `arcvpn-${String(locationLabel).replace(/\s/g, '-')}.conf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const copyToClipboard = async () => {
    if (!config?.content) return;
    try {
      await navigator.clipboard.writeText(config.content);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className="cm-overlay" onClick={onClose} role="presentation">
      <div
        className={`cm-modal ${config ? 'is-ready' : ''}`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="cm-title"
      >
        <header className="cm-header">
          <div>
            <p className="cm-kicker">
              {config ? t('connectModal.kickerReady') : t('connectModal.kickerSecure')}
            </p>
            <h2 id="cm-title">{title}</h2>
          </div>
          <button type="button" className="cm-close" onClick={onClose} aria-label={t('connectModal.close')}>
            ×
          </button>
        </header>

        <div className="cm-body">
          <article className="cm-endpoint">
            <div className="cm-flag" aria-hidden="true">
              {flag}
            </div>
            <div className="cm-endpoint-meta">
              <h3>{node?.name || locationLabel}</h3>
              <p>{locationLabel}</p>
              <div className="cm-metrics">
                <span className="cm-metric">
                  <em>{t('connectModal.load')}</em>
                  <strong className={`is-${loadTone(load)}`}>{load}%</strong>
                </span>
                {node?.protocol && (
                  <span className="cm-metric">
                    <em>{t('connectModal.protocol')}</em>
                    <strong>{String(node.protocol).toUpperCase()}</strong>
                  </span>
                )}
                <span className={`cm-pill ${config ? 'ok' : 'idle'}`}>
                  {config ? t('connectModal.configReady') : t('connectModal.waiting')}
                </span>
              </div>
            </div>
          </article>

          {error && (
            <div className="cm-error" role="alert">
              {error}
            </div>
          )}

          {!config ? (
            <>
              <p className="cm-lead">{t('connectModal.leadBefore')}</p>
              <ul className="cm-steps">
                <li>{t('connectModal.step1')}</li>
                <li>{t('connectModal.step2')}</li>
                <li>{t('connectModal.step3')}</li>
              </ul>
            </>
          ) : (
            <>
              <p className="cm-lead">{t('connectModal.leadAfter')}</p>

              <div className="cm-tabs" role="tablist">
                <button
                  type="button"
                  role="tab"
                  aria-selected={showQR}
                  className={`cm-tab ${showQR ? 'is-active' : ''}`}
                  onClick={() => setShowQR(true)}
                >
                  {t('connectModal.tabQr')}
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={!showQR}
                  className={`cm-tab ${!showQR ? 'is-active' : ''}`}
                  onClick={() => setShowQR(false)}
                >
                  {t('connectModal.tabConfig')}
                </button>
              </div>

              {showQR ? (
                <div className="cm-qr">
                  <div className="cm-qr-frame">
                    <QRCode value={config.content} size={196} level="M" />
                  </div>
                  <p>{t('connectModal.scanHint')}</p>
                </div>
              ) : (
                <div className="cm-config">
                  <pre>{config.content}</pre>
                  <button type="button" className="cm-btn ghost" onClick={copyToClipboard}>
                    {copied ? t('connectModal.copied') : t('connectModal.copyConfig')}
                  </button>
                </div>
              )}

              <dl className="cm-details">
                <div>
                  <dt>{t('connectModal.vpnIp')}</dt>
                  <dd>{config.details?.client_ip || '—'}</dd>
                </div>
                <div>
                  <dt>{t('connectModal.server')}</dt>
                  <dd>{config.details?.server_endpoint || '—'}</dd>
                </div>
                <div>
                  <dt>{t('connectModal.dns')}</dt>
                  <dd>{config.details?.dns || '1.1.1.1, 8.8.8.8'}</dd>
                </div>
              </dl>
            </>
          )}
        </div>

        <footer className="cm-footer">
          {!config ? (
            <>
              <button type="button" className="cm-btn ghost" onClick={onClose} disabled={loading}>
                {t('connectModal.cancel')}
              </button>
              <button
                type="button"
                className="cm-btn primary"
                onClick={handleQuickConnect}
                disabled={loading}
              >
                {loading ? t('connectModal.connecting') : t('connectModal.connect')}
              </button>
            </>
          ) : (
            <>
              <button type="button" className="cm-btn ghost" onClick={downloadConfig}>
                {t('connectModal.downloadConf')}
              </button>
              <button type="button" className="cm-btn primary" onClick={onClose}>
                {t('connectModal.done')}
              </button>
            </>
          )}
        </footer>
      </div>
    </div>
  );
};

export default ConnectModal;
